import { Table, TableColumn } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import {
  LIMITS,
  RESOURCE_FIELDS,
  ResourceField,
  ServiceValues,
  WorkloadValues,
  environmentLabel,
  environmentsWith,
  resourceValue,
  workloadValuesFor,
} from '@internal/backstage-plugin-release-common';
import { Cell, Draft, cellKey } from './draft';

export const SCALING_LABELS: Record<ResourceField | 'replicaCount', string> = {
  replicaCount: 'Replicas',
  'requests.cpu': 'CPU request',
  'requests.memory': 'Memory request',
  'limits.memory': 'Memory limit',
};

interface Row {
  field: ResourceField | 'replicaCount';
  label: string;
  perEnvironment: Record<string, WorkloadValues | undefined>;
}

function Replicas({ workload }: { workload: WorkloadValues | undefined }) {
  if (!workload) {
    return <>—</>;
  }
  const hpa = workload.autoscaling;
  if (hpa?.enabled) {
    // The chart ignores replicaCount while the autoscaler is on, so showing
    // the number would show something that is not what runs.
    return (
      <Tooltip title="Autoscaled: replicaCount is ignored while the HPA is enabled.">
        <span>
          HPA {hpa.minReplicas ?? '?'}–{hpa.maxReplicas ?? '?'}
        </span>
      </Tooltip>
    );
  }
  return <>{workload.replicaCount ?? '—'}</>;
}

interface Props {
  values: ServiceValues;
  workload: string;
  editing?: {
    draft: Draft;
    onDraft: (cell: Cell, value: string) => void;
  };
}

/** Replicas and resources of one workload, one column per environment. */
export const ScalingSection = ({ values, workload, editing }: Props) => {
  const environments = environmentsWith(values, workload);
  const perEnvironment = Object.fromEntries(
    environments.map(environment => [
      environment,
      workloadValuesFor(values, environment, workload),
    ]),
  );

  const rows: Row[] = [
    { field: 'replicaCount', label: SCALING_LABELS.replicaCount, perEnvironment },
    ...RESOURCE_FIELDS.map(field => ({
      field,
      label: SCALING_LABELS[field],
      perEnvironment,
    })),
  ];

  const cellOf = (row: Row, environment: string): Cell =>
    row.field === 'replicaCount'
      ? { environment, workload, kind: 'replicaCount' }
      : { environment, workload, kind: 'resource', field: row.field };

  const columns: TableColumn<Row>[] = [
    { title: '', field: 'label', sorting: false },
    ...environments.map(
      (environment): TableColumn<Row> => ({
        title: environmentLabel(environment),
        field: `perEnvironment.${environment}`,
        sorting: false,
        render: row => {
          const entry = row.perEnvironment[environment];
          const autoscaled = row.field === 'replicaCount' && entry?.autoscaling?.enabled;
          if (!editing || !entry || autoscaled) {
            if (row.field === 'replicaCount') {
              return <Replicas workload={entry} />;
            }
            const value = entry ? resourceValue(entry.resources, row.field) : undefined;
            return value ?? (
              <Typography variant="body2" color="textSecondary" component="span">
                unset
              </Typography>
            );
          }
          const cell = cellOf(row, environment);
          const current =
            row.field === 'replicaCount'
              ? String(entry.replicaCount ?? '')
              : resourceValue(entry.resources, row.field) ?? '';
          return (
            <TextField
              size="small"
              variant="outlined"
              type={row.field === 'replicaCount' ? 'number' : 'text'}
              value={editing.draft[cellKey(cell)] ?? current}
              inputProps={{
                'aria-label': `${row.label} in ${environmentLabel(environment)}`,
                ...(row.field === 'replicaCount'
                  ? { min: LIMITS.replicas.min, max: LIMITS.replicas.max }
                  : {}),
              }}
              onChange={event => editing.onDraft(cell, event.target.value)}
            />
          );
        },
      }),
    ),
  ];

  return (
    <>
      <Table
        options={{
          search: false,
          paging: false,
          toolbar: false,
          padding: 'dense',
        }}
        columns={columns}
        data={rows}
      />
      {editing && (
        <Box px={2} py={1}>
          <Typography variant="caption" color="textSecondary">
            Replicas {LIMITS.replicas.min}–{LIMITS.replicas.max} · CPU{' '}
            {LIMITS.cpuMillicores.min}m–{LIMITS.cpuMillicores.max}m · Memory{' '}
            {LIMITS.memoryMi.min}Mi–{LIMITS.memoryMi.max / 1024}Gi, limit at or above
            the request. Replicas are read-only where the autoscaler is on.
          </Typography>
        </Box>
      )}
    </>
  );
};
